import { BusinessFunctionDropdown } from 'domain/shared/businessFunction';
import { isTechnologyRequired } from 'domain/shared/career';
import { EMPLOYEE_CONTRACT_ASSIGMENT_TYPES, PositionDropdown, TBILISI_OFFICE_ID } from 'domain/shared/employee';
import { OfficeCheckboxGroup, POSITION_SLOT_OFFICES_CHECKBOX_SX } from 'domain/shared/employee/office';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import { EnumDropdown, InputList } from 'framework/form';
import type { CreateEditFormSection } from 'framework/ViewTypes/contentLayout';
import PositionProfileDropdown from '../../../positionProfiles/shared/input/PositionProfileDropdownWithDeps';
import { POSITION_SLOT_WORKER_TYPE_CREATE_LABELS } from '../constants';
import type { PositionSlotFormValues } from '../types';
import SecondaryCompetencyRowInput from './inputs/SecondaryCompetencyRowInput';
import PositionSlotTechnologyDropdown from './inputs/PositionSlotTechnologyDropdown';

const fieldSx = {
  '& .floating-label-input, & .floating-label-baseInput, & .floating-label-dropdown': { mb: 0 },
};

type PositionSlotFormSectionsParams = Partial<
  Pick<PositionSlotFormValues, 'specializationId' | 'technologyId' | 'competencyLevel'>
> & {
  definitionDisabled?: boolean;
  locationDisabled?: boolean;
};

export const getPositionSlotFormSections = ({
  specializationId,
  technologyId,
  competencyLevel,
  definitionDisabled = false,
  locationDisabled = false,
}: PositionSlotFormSectionsParams): CreateEditFormSection[] => [
  {
    id: 'position-definition',
    fields: [
      {
        id: 'specializationId',
        sx: fieldSx,
        content: (
          <PositionDropdown source="specializationId" label="Specialization" isRequired disabled={definitionDisabled} />
        ),
      },
      ...(isTechnologyRequired(specializationId)
        ? [
            {
              id: 'technologyId',
              sx: fieldSx,
              content: <PositionSlotTechnologyDropdown disabled={definitionDisabled} />,
            },
          ]
        : []),
      {
        id: 'competencyLevel',
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="competencyLevel"
            label="Level"
            options={EMPLOYEE_COMPETENCY_LEVEL}
            isRequired
            disabled={definitionDisabled}
          />
        ),
      },
      {
        id: 'secondaryCompetencies',
        size: { xs: 12, md: 12, xl: 12 },
        content: (
          <InputList
            source="secondaryCompetencies"
            label="Secondary competencies"
            inputTemplate={SecondaryCompetencyRowInput}
            addText="Add secondary competency"
            defaultValue={{}}
            maxFields={50}
            rowSx={{ '&:last-of-type': { mb: 2 } }}
            disabled={definitionDisabled}
          />
        ),
      },
    ],
  },
  {
    id: 'position-employment',
    fields: [
      {
        id: 'businessFunctionId',
        sx: fieldSx,
        content: <BusinessFunctionDropdown source="businessFunctionId" label="Business function" isRequired />,
      },
      {
        id: 'workerType',
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="workerType"
            label="Category"
            options={POSITION_SLOT_WORKER_TYPE_CREATE_LABELS}
            isRequired
            disabled={definitionDisabled}
          />
        ),
      },
      {
        id: 'workRateType',
        sx: fieldSx,
        content: (
          <EnumDropdown
            source="workRateType"
            label="FTE"
            options={EMPLOYEE_CONTRACT_ASSIGMENT_TYPES}
            isRequired
            disabled={definitionDisabled}
          />
        ),
      },
    ],
  },
  {
    id: 'position-profile-and-location',
    fields: [
      {
        id: 'positionProfileId',
        sx: fieldSx,
        content: (
          <PositionProfileDropdown
            source="positionProfileId"
            label="Position profile"
            specializationId={specializationId}
            technologyId={technologyId}
            competencyLevel={competencyLevel}
            // TODO: Restore isRequired before the official production release (temporary UI fix).
            isRequired={false}
          />
        ),
      },
      {
        id: 'officeIds',
        size: { xs: 12, md: 12, xl: 12 },
        content: (
          <OfficeCheckboxGroup
            source="officeIds"
            label="Location:"
            isRequired
            sx={POSITION_SLOT_OFFICES_CHECKBOX_SX}
            disabled={locationDisabled}
            excludeAnywhere
            excludedOfficeIds={[TBILISI_OFFICE_ID]}
          />
        ),
      },
    ],
  },
];
